<template>
  <div class="chat-body" v-if="actionState === 'summary'">
    <w-banner
      v-if="sessionTimestamp !== today && reviewStatus === 'pending'"
      :dismissible="false"
      variant="warning"
      class="warning-banner"
    >
      <strong>Atenção!</strong> Existe um resumo de
      <span v-text="moment(sessionTimestamp).format('DD/MM/YYYY')"></span> deste paciente que não foi salvo. Deseja
      continuar de onde parou?
    </w-banner>

    <div class="container-summary">
      <p>
        Seu resumo automático está pronto. Ao utilizar estas anotações, você confirma para nós, que ele está preciso e
        representa o que ocorreu durante a consulta.
      </p>

      <div class="badge-container container-item">
        <div class="memo-header">
          <div class="actions-wrapper">
            <w-button
              variant="plain"
              label="Regerar resumo"
              leading-icon="repeat"
              :disabled="isRegenerating"
              :loading="isRegenerating"
              class="regenerate-button"
              @click="handleRegenerateSummary"
            />
            <w-button
              variant="plain"
              label="Copiar"
              leading-icon="clipboard"
              @click="handleCopyClick"
              class="copy-button"
            />
          </div>
        </div>
      </div>

      <div class="editor-container container-item">
        <QuillEditor
          ref="quillEditor"
          theme="snow"
          :content="summaryText"
          contentType="html"
          :options="quillOptions"
          @update:content="$emit('update:summaryText', $event)"
          @keydown="handleKeyDown"
        />
      </div>
    </div>
    <w-toast :toasts="toastsData" />
  </div>
</template>

<script setup lang="ts">
import { ref, inject, onMounted } from "vue";
import { QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import moment from "moment";
import { formatSummaryData } from "@/utils/formatters";
import { UserTracking } from "@/utils/UserTracking";
import { trackNoaRumAction, NOA_RUM_EVENT } from "@/utils/noaObservability";

interface ApiClient {
  get: (url: string, params?: any) => Promise<any>;
  post: (url: string, data?: any) => Promise<any>;
}

const apiClient = inject<ApiClient | null>("apiClient", null);

const props = defineProps({
  actionState: {
    type: String,
    required: true,
  },
  reviewStatus: {
    type: String,
    required: true,
  },
  summaryText: {
    type: String,
    required: true,
  },
  sessionTimestamp: {
    type: String,
    required: true,
  },
  initialContext: {
    type: Object,
    required: true,
  },
});

const quillEditor = ref(null);
const toastsData = ref([]);
const isRegenerating = ref(false);
let editDebounceTimer: ReturnType<typeof setTimeout> | null = null;

const emit = defineEmits(["update:summaryText"]);

const today = new Date().toISOString().split("T")[0];

onMounted(() => {
  if (props.summaryText && props.summaryText.trim().length > 0) {
    UserTracking.trackEvent("noa_notes_summary_opened", {
      session_id: props.initialContext?.recorderSessionId ?? "unknown",
      review_status: props.reviewStatus,
      page_category: "noa notes",
      page_url: window.location.href,
    });
    trackNoaRumAction(NOA_RUM_EVENT.SUMMARY_VIEWED, {
      session_id: props.initialContext?.recorderSessionId ?? "unknown",
      review_status: props.reviewStatus,
    });
  }
});

const quillOptions = {
  modules: {
    toolbar: ["bold", "italic", "underline", { list: "ordered" }, { list: "bullet" }],
  },
  placeholder: "Digite...",
  theme: "snow",
};

const handleRegenerateSummary = async () => {
  const sessionId = props.initialContext?.recorderSessionId;
  if (!apiClient || !sessionId) return;

  isRegenerating.value = true;
  try {
    const response = await apiClient.get(`/session/${sessionId}/regenerate-summary`);
    const formattedSummary = formatSummaryData(JSON.parse(response.content).result);
    emit("update:summaryText", formattedSummary);
  } catch (err) {
    console.error("Failed to regenerate summary:", err);
    toastsData.value = [{ content: "Erro ao regenerar resumo.", variant: "error", timeOut: 3000 }];
  } finally {
    isRegenerating.value = false;
  }
};

const handleCopyClick = async () => {
  try {
    const textToCopy = quillEditor?.value?.getText();

    await navigator.clipboard.writeText(textToCopy);
    toastsData.value = [];

    const newToast = { content: "Texto copiado com sucesso!", variant: "default", timeOut: 3000 };

    toastsData.value.push(newToast);

    UserTracking.trackEvent("summary_copied", {
      action: "mouse click copy",
      session_id: props.initialContext?.recorderSessionId ?? "unknown",
      page_category: "noa notes",
      page_section: "",
      page_url: window.location.href,
    });
  } catch (err) {
    console.error("Failed to copy text:", err);
  }
};

const handleKeyDown = (e) => {
  // Ctrl+C / Cmd+C → copy
  if ((e.ctrlKey || e.metaKey) && e.key === "c") {
    UserTracking.trackEvent("summary_copied", {
      action: "keyboard copy",
      session_id: props.initialContext?.recorderSessionId ?? "unknown",
      page_category: "noa notes",
      page_section: "",
      page_url: window.location.href,
    });
    return;
  }

  // Detect actual text-modifying keys, ignore navigation/modifier-only events
  const isEditKey = e.key.length === 1 || ["Backspace", "Delete", "Enter"].includes(e.key);
  if (!isEditKey) return;

  if (editDebounceTimer) clearTimeout(editDebounceTimer);
  editDebounceTimer = setTimeout(() => {
    UserTracking.trackEvent("noa_notes_summary_edited", {
      session_id: props.initialContext?.recorderSessionId ?? "unknown",
      page_category: "noa notes",
      page_url: window.location.href,
    });
    trackNoaRumAction(NOA_RUM_EVENT.SUMMARY_EDITED, {
      session_id: props.initialContext?.recorderSessionId ?? "unknown",
    });
  }, 1000);
};
</script>

<style scoped lang="scss">
.chat-body {
  height: 100%;
  padding: 16px;
  padding-bottom: 100px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.container-summary {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  :deep(.quill-editor) {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 200px;

    .ql-container {
      flex: 1;
      overflow-y: auto;
    }
  }
}

.warning-banner {
  margin-bottom: 20px;
  margin-top: 10px;
}

.badge-container {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 8px;
}

.memo-header {
  display: flex;
  align-items: center;
}

.actions-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 4px 0 4px 0;
}

.regenerate-button,
.copy-button {
  font-size: 13px;
  padding: 4px 12px;

  :deep(.w-icon) {
    width: 14px;
    height: 14px;
  }
}

.content-container {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 16px;
  height: 100%;
}

.editor-container {
  height: 100%;
  min-height: 200px;
}

.summary-header {
  margin-bottom: 16px;
}

.integration-highlight {
  margin-top: 12px;
  color: var(--w-color-success);
  font-weight: 500;
}

.editor-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.container-summary > *:last-child {
  flex: 0.9;
}

</style>
